import { useContext, useEffect, useState } from "react";

import DetailsContainer from "components/TableDetails/components/DetailsContainer";
import AuthContext from "context/auth/authContext";
import TableContext from "context/table/tableContext";
import { API_IS_BEING_CANCELLED } from "shared/constants/strings";
import { getEnvironment } from "shared/functions";
import { useApi } from "shared/hooks/useApi";
import { useIsMounted } from "shared/hooks/useIsMounted";
import { ENDPOINTS } from "shared/types/enums";

import {
    Accessories,
    Can,
    CanAdapter,
    Carrier,
    ConfigurationDetailsAndSim,
    General,
    Obd,
    Sim
} from "./Cards";
import Loader from "./Loader";
import { DetailsData, DetailsProps } from "./types";

const Details = ({ imei }: DetailsProps) => {
    const { isAdminUser, isCarrierUser } = useContext(AuthContext);
    const { detailsData, setDetailsData } = useContext(TableContext);

    const { getData, handleResponse, cancelSource, isCanceled } = useApi();
    const isMounted = useIsMounted();

    const { isProductionEnv } = getEnvironment();

    const {
        accessories,
        can,
        can_adapter,
        can_package,
        carrier,
        obd
    }: DetailsData = detailsData;

    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const getAdditionalData = async () => {
            try {
                setIsLoading(true);

                const { data } = await getData(`${ENDPOINTS.Devices}/${imei}`);

                const {
                    ble_dtb_version,
                    bluetooth_authorization_list,
                    bootloader_version,
                    can,
                    can_package,
                    dtb_version,
                    manual_geofence_list,
                    "1_wire_authorization_list": one_wire_authorization_list
                } = data;

                if (isMounted) {
                    setDetailsData({
                        ...detailsData,
                        ble_dtb_version,
                        bluetooth_authorization_list,
                        bootloader_version,
                        can,
                        can_package,
                        dtb_version,
                        manual_geofence_list,
                        one_wire_authorization_list
                    });
                }
            } catch (error) {
                if (isMounted && !isCanceled(error)) {
                    handleResponse(error);
                }
            }

            setIsLoading(false);
        };

        getAdditionalData();

        return () => {
            cancelSource.cancel(API_IS_BEING_CANCELLED);
        };

        // eslint-disable-next-line
    }, []);

    const renderObd = !!obd?.vin || !!obd?.chip_version;

    const renderCan =
        !!can_package ||
        !!can?.can_package ||
        !!can?.chip_version ||
        !!can?.vehicle ||
        !!can?.vehicle_id ||
        !!can?.can_package_id;

    const renderCarrier = (isAdminUser || isCarrierUser) && carrier;

    return (
        <DetailsContainer>
            {isLoading ? (
                <Loader />
            ) : (
                <>
                    <General />
                    {isProductionEnv ? <ConfigurationDetailsAndSim /> : <Sim />}

                    {renderObd && <Obd />}
                    {can_adapter && <CanAdapter />}
                    {renderCan && <Can />}
                    {accessories && <Accessories />}
                    {renderCarrier && <Carrier />}
                </>
            )}
        </DetailsContainer>
    );
};

export default Details;
export * from "./functions";
